/**
 * uygulama.tsx
 *
 * NE İŞE YARAR : Arayüzün kök bileşeni — oturum sağlayıcısı ve yol tablosu
 * KAYNAK SPEC  : 15-arayuz-standardi.md §5 (navigasyon) · §5.2 (menü) · §15 (kimlik
 *                ekranları) · §7.1 (yetkisi olmayan hiç görmez) · 04-api-sozlesmesi.md
 * BAĞLI OLDUĞU : react-router-dom · src/oturum/* · src/kabuk/* · src/ekran/**
 * KULLANAN     : src/main.tsx
 * DİKKAT       : 1) YOL TABLOSU İKİYE AYRILIR: kimlik yolları oturumsuzdur, geri kalanı
 *                   `KorumaliYol` + `Kabuk` içindedir. Ayrım tek yerde durur; her ekranın
 *                   kendi oturum denetimini yapması, denetimi unutan bir ekranın sessizce
 *                   açılması demekti.
 *                2) KORUMALI YOLLAR **İÇ YOL**DUR (`Outlet`). Kabuk bir kez çizilir ve
 *                   ekranlar onun içinde değişir; her ekranın kabuğu kendi çizmesi,
 *                   sayfa geçişlerinde menünün ve scope seçicinin yeniden kurulması
 *                   demekti — seçili kurum her tıklamada sıfırlanırdı.
 *                3) MENÜDEKİ HER YOLUN BİR EKRANI VAR ve 2026-08-31 06:05:00 itibarıyla
 *                   hiçbiri artık *"yazılmadı"* sayfası değil. `YazilmadiEkrani`
 *                   dosyası **kaldırıldı**: kullanılmayan bir yer tutucu, bir sonraki
 *                   okuyana hâlâ eksik bir şey olduğunu düşündürürdü.
 *                7) DETAY YOLLARI MENÜDE **YOKTUR** ve olmamalı: menü *"ne
 *                   yapabiliyorsan o"* listesidir (`15` §5.2), tek bir kaydın adresi
 *                   değil. Detaya listeden ya da paylaşılan bir bağlantıdan girilir.
 *                4) `/cihazlarim` KABUĞUN İÇİNDEDİR ama menüde değildir: `SELF` bir
 *                   ekrandır ve kullanıcı menüsünden girilir (`kabuk.tsx`). Modül menüsü
 *                   *"ne yapabiliyorsan o"* listesidir; kendi cihazını yönetmek bir
 *                   yetki değildir.
 *                5) `/tasarim` GELİŞTİRİCİ ARACIDIR, oturumsuzdur ve kabuksuzdur:
 *                   doğrulanan şey renk ve yazı tipi, veri değil.
 *                6) BİLİNMEYEN YOL ANA EKRANA GİDER, HATA SAYFASINA DEĞİL. Faz 1'de
 *                   dışarıya bağlantı verilmiyor; yanlış adres yazan kullanıcı için
 *                   gidebileceği yeri göstermek daha çok işe yarar.
 * SON GÜNCELLEME: 2026-08-31 06:05:00
 */
import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Kabuk } from './kabuk/kabuk';
import { Iskelet } from './ortak/durum';
import { OturumSaglayici } from './oturum/baglam';
import { KimlikYolu, KorumaliYol } from './oturum/koruma';

/*
 * 44. ADIM — YOL BAZLI KOD BÖLME
 *
 * Paket 602 kB'a çıkmıştı ve Vite'ın 500 kB eşiğini aşıyordu. Sebep basit: uygulama
 * **bütün ekranları** tek dosyada taşıyordu, kullanıcı yalnız birini açsa bile.
 *
 * Kimlik ekranları BÖLÜNMEZ ve bu bilinçlidir: giriş, ilk açılışta görülen tek
 * ekrandır; onu ayrı bir parçaya koymak, en kritik anda **fazladan** bir istek
 * eklerdi. Bölünen şey oturum açıldıktan sonra görülen modül ekranlarıdır.
 *
 * `lazy` yalnız **varsayılan dışa aktarımı** anlar; ekranlar adlandırılmış dışa
 * aktarım kullanıyor ve `bolumle` bu ikisini birbirine bağlıyor. Ekranların
 * varsayılan dışa aktarıma çevrilmesi de mümkündü ama o zaman her dosyanın adı
 * içeriğinden okunamaz hâle gelirdi.
 */
function bolumle(
  yukle: () => Promise<Record<string, unknown>>,
  ad: string,
): ComponentType {
  return lazy(async () => ({ default: (await yukle())[ad] as ComponentType }));
}

// --- Kimlik ekranları: bölünmez (yukarıdaki not) ------------------------
import { CihazlarimEkrani } from './ekran/kimlik/cihazlarim';
import { DogrulamaEkrani } from './ekran/kimlik/dogrulama';
import { GirisEkrani } from './ekran/kimlik/giris';
import { SifirlamaEkrani } from './ekran/kimlik/sifirlama';
import { SifirlamaTalebiEkrani } from './ekran/kimlik/sifirlama-talebi';

// --- Modül ekranları: yol başına ayrı parça ----------------------------
const AnaEkran = bolumle(() => import('./ekran/ana-ekran'), 'AnaEkran');
const BasvuruDetay = bolumle(() => import('./ekran/basvuru/basvuru-detay'), 'BasvuruDetay');
const BasvuruListesi = bolumle(() => import('./ekran/basvuru/basvuru-listesi'), 'BasvuruListesi');
const DonemListesi = bolumle(() => import('./ekran/basvuru/donem-listesi'), 'DonemListesi');
const KisiDetay = bolumle(() => import('./ekran/kisi/kisi-detay'), 'KisiDetay');
const KisiListesi = bolumle(() => import('./ekran/kisi/kisi-listesi'), 'KisiListesi');
const KullaniciListesi = bolumle(() => import('./ekran/iam/iam-ekranlari'), 'KullaniciListesi');
const OturumListesi = bolumle(() => import('./ekran/iam/iam-ekranlari'), 'OturumListesi');
const RolListesi = bolumle(() => import('./ekran/iam/iam-ekranlari'), 'RolListesi');
const KurumListesi = bolumle(() => import('./ekran/kurum/kurum-listesi'), 'KurumListesi');
const DenetimListesi = bolumle(() => import('./ekran/kvkk/kvkk-ekranlari'), 'DenetimListesi');
const GuvenlikOlayiListesi = bolumle(
  () => import('./ekran/kvkk/kvkk-ekranlari'),
  'GuvenlikOlayiListesi',
);
const HukukiMuhafazaListesi = bolumle(
  () => import('./ekran/kvkk/kvkk-ekranlari'),
  'HukukiMuhafazaListesi',
);
const VeriTalebiListesi = bolumle(() => import('./ekran/kvkk/kvkk-ekranlari'), 'VeriTalebiListesi');
const OgrenciDetay = bolumle(() => import('./ekran/ogrenci/ogrenci-detay'), 'OgrenciDetay');
const OgrenciListesi = bolumle(() => import('./ekran/ogrenci/ogrenci-listesi'), 'OgrenciListesi');
const GecisListesi = bolumle(() => import('./ekran/gecis/gecis-ekrani'), 'GecisListesi');
const IzinListesi = bolumle(() => import('./ekran/izin/izin-ekrani'), 'IzinListesi');
const IzinDetay = bolumle(() => import('./ekran/izin/izin-ekrani'), 'IzinDetay');
const YoklamaListesi = bolumle(() => import('./ekran/yoklama/yoklama-ekrani'), 'YoklamaListesi');
const YoklamaDetay = bolumle(() => import('./ekran/yoklama/yoklama-ekrani'), 'YoklamaDetay');
const YoklamaSaatleri = bolumle(
  () => import('./ekran/yoklama/yoklama-ekrani'),
  'YoklamaSaatleri',
);
const ZiyaretListesi = bolumle(() => import('./ekran/ziyaret/ziyaret-ekrani'), 'ZiyaretListesi');
const YapiEkrani = bolumle(() => import('./ekran/yapi/yapi-ekrani'), 'YapiEkrani');
const YerlesimDetay = bolumle(() => import('./ekran/yerlesim/yerlesim-listesi'), 'YerlesimDetay');
const YerlesimListesi = bolumle(
  () => import('./ekran/yerlesim/yerlesim-listesi'),
  'YerlesimListesi',
);
const OturumOzetiEkrani = bolumle(() => import('./ekran/oturum-ozeti'), 'OturumOzetiEkrani');
const TasarimEkrani = bolumle(() => import('./ekran/tasarim'), 'TasarimEkrani');

export function Uygulama(): ReactElement {
  return (
    <OturumSaglayici>
      <Routes>
        {/* Kimlik yolları — oturumsuz, kabuksuz (başlıktaki not 1). */}
        <Route
          path="/giris"
          element={
            <KimlikYolu>
              <GirisEkrani />
            </KimlikYolu>
          }
        />
        <Route
          path="/giris/dogrulama"
          element={
            <KimlikYolu>
              <DogrulamaEkrani />
            </KimlikYolu>
          }
        />
        <Route path="/parola/sifirlama-talebi" element={<SifirlamaTalebiEkrani />} />
        {/*
          Sıfırlama ekranı `KimlikYolu` ile sarılMAZ: bağlantıyı açan kullanıcının
          tarayıcısında eski bir oturum açık olabilir ve `AU-05` zaten bütün oturumları
          iptal edecektir. Sarılsaydı, parolasını sıfırlamaya gelen kullanıcı bir önceki
          oturumunun ana ekranına atılır ve bağlantı işe yaramaz görünürdü.
        */}
        <Route path="/parola/sifirlama" element={<SifirlamaEkrani />} />

        {/* Geliştirici aracı (başlıktaki not 5). */}
        <Route path="/tasarim" element={<TasarimEkrani />} />

        {/* Oturum isteyen yollar — hepsi kabuğun içinde (başlıktaki not 2). */}
        <Route
          element={
            <KorumaliYol>
              {/*
                Bölünen parça yüklenirken **iskelet** gösteriliyor, boş sayfa değil:
                boş sayfa, yavaş bağlantıda uygulamanın çöktüğü izlenimi verirdi.
                `Suspense` kabuğun İÇİNDE: üst çubuk ve menü yerinde kalır, yalnız
                içerik alanı bekler.
              */}
              <Suspense fallback={<Iskelet />}>
                <Kabuk />
              </Suspense>
            </KorumaliYol>
          }
        >
          <Route path="/" element={<AnaEkran />} />
          {/*
            Oturum özeti ana ekranın yerini tutuyordu; ana ekran `15` §6'ya göre
            yazılınca `/oturum` altına alındı. **Silinmedi** çünkü işlevi değişti:
            menünün neden öyle kurulduğu, ancak `X-02`nin döndürdüğü izin kümesi
            görülerek anlaşılır (`15` §7.2) ve bunu gösteren tek yer orası.
          */}
          <Route path="/oturum" element={<OturumOzetiEkrani />} />
          <Route path="/cihazlarim" element={<CihazlarimEkrani />} />

          {/* 19. adım — modül ekranları 1 (kişi · başvuru · belge · öğrenci). */}
          <Route path="/kisiler" element={<KisiListesi />} />
          <Route path="/kisiler/:id" element={<KisiDetay />} />
          <Route path="/basvurular" element={<BasvuruListesi />} />
          <Route path="/basvurular/:id" element={<BasvuruDetay />} />
          <Route path="/basvuru-donemleri" element={<DonemListesi />} />
          <Route path="/ogrenciler" element={<OgrenciListesi />} />
          <Route path="/ogrenciler/:id" element={<OgrenciDetay />} />

          {/* 98.–101. adımlar — Faz 2 ekranları. */}
          <Route path="/gecisler" element={<GecisListesi />} />
          <Route path="/izinler" element={<IzinListesi />} />
          <Route path="/izinler/:id" element={<IzinDetay />} />
          <Route path="/yoklamalar" element={<YoklamaListesi />} />
          <Route path="/yoklamalar/:id" element={<YoklamaDetay />} />
          <Route path="/yoklama-saatleri" element={<YoklamaSaatleri />} />
          <Route path="/ziyaretler" element={<ZiyaretListesi />} />

          {/* 20. adım — modül ekranları 2 (yapı · yerleşim · IAM · KVKK · kurum). */}
          <Route path="/yapi" element={<YapiEkrani />} />
          <Route path="/yerlesimler" element={<YerlesimListesi />} />
          <Route path="/yerlesimler/:id" element={<YerlesimDetay />} />
          <Route path="/kurumlar" element={<KurumListesi />} />
          <Route path="/kullanicilar" element={<KullaniciListesi />} />
          <Route path="/roller" element={<RolListesi />} />
          <Route path="/oturumlar" element={<OturumListesi />} />
          <Route path="/denetim" element={<DenetimListesi />} />
          <Route path="/guvenlik-olaylari" element={<GuvenlikOlayiListesi />} />
          <Route path="/veri-talepleri" element={<VeriTalebiListesi />} />
          <Route path="/hukuki-muhafaza" element={<HukukiMuhafazaListesi />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </OturumSaglayici>
  );
}
